import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Badge,
  Table,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from 'reactstrap'
import { motion } from 'framer-motion'
import { 
  FaArrowLeft, 
  FaEye, 
  FaEdit, 
  FaExchangeAlt, 
  FaHistory,
  FaFileInvoiceDollar,
  FaHashtag
} from 'react-icons/fa'
import { fetchMyProposals } from '../../store/slices/transportRequestSlice'
import CounterProposal from './CounterProposal'
import ReviseProposal from './ReviseProposal'

const MyProposals = () => {
  const { id } = useParams()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { proposals, loading } = useSelector(state => state.transportRequest)

  const [selectedProposal, setSelectedProposal] = useState(null)
  const [detailsModal, setDetailsModal] = useState(false)
  const [counterProposalModal, setCounterProposalModal] = useState(false)
  const [reviseProposalModal, setReviseProposalModal] = useState(false)

  useEffect(() => {
    dispatch(fetchMyProposals(id))
  }, [dispatch, id])

  const getStatusBadge = status => {
    const statusConfig = {
      draft: { color: 'secondary', label: 'Draft' },
      submitted: { color: 'warning', label: 'Submitted' },
      under_review: { color: 'info', label: 'Under Review' },
      counter_proposed: { color: 'primary', label: 'Counter Proposed' },
      accepted: { color: 'success', label: 'Accepted' },
      rejected: { color: 'danger', label: 'Rejected' },
    }
    const config = statusConfig[status] || { color: 'secondary', label: status }
    return <Badge color={config.color} className="status-pill">{config.label}</Badge>
  }

  const getVersionBadge = version => {
    const versionNum = version?.split('-V')[1] || '1'
    return (
      <Badge color="dark" className="version-badge bg-glass border-glass">
        V{versionNum}
      </Badge>
    )
  }

  const handleViewDetails = proposal => {
    setSelectedProposal(proposal)
    setDetailsModal(true)
  }

  const handleViewCounter = proposal => {
    setSelectedProposal(proposal)
    setCounterProposalModal(true)
  }

  const handleRevise = proposal => {
    setSelectedProposal(proposal)
    setReviseProposalModal(true)
  }

  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center py-5">
            <Spinner color="primary" />
          </div>
        </Container>
      </div>
    )
  }

  return (
    <div className="page-content">
      <Container fluid>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="page-header d-flex flex-column mb-4">
          <Button
            color="link"
            className="p-0 mb-3 text-decoration-none d-inline-flex align-items-center text-primary fw-bold"
            onClick={() => navigate(`/transport-request/${id}`)}
          >
            <FaArrowLeft className="me-2" />
            Back to Manifest Intelligence
          </Button>
          <div className="d-flex justify-content-between align-items-center">
            <h2 className="page-title neo-text m-0">Negotiation Stream</h2>
            <p className="m-0 text-muted">Auditing all protocol versions for this manifest</p>
          </div>
        </motion.div>

        {proposals.length === 0 ? (
          <Card className="aurora-card">
            <CardBody className="text-center py-5">
              <FaFileInvoiceDollar size={48} className="text-muted opacity-20 mb-3" />
              <h5 className="neo-text">No Protocol Logs Found</h5>
              <p className="text-muted mt-2">Initiate a bid to start the negotiation stream.</p>
            </CardBody>
          </Card>
        ) : (
          <div className="glass-effect rounded-3 overflow-hidden border-glass mb-4">
            <Table borderless responsive hover className="mb-0 custom-table">
              <thead>
                <tr>
                  <th><FaHashtag className="me-2" /> ID</th>
                  <th>Version</th>
                  <th>Rate Protocol</th>
                  <th>Advance %</th>
                  <th>Total Freight</th>
                  <th>Status</th>
                  <th>Timestamp</th>
                  <th className="text-center">Protocol Actions</th>
                </tr>
              </thead>
                <tbody>
                  {proposals.map(proposal => (
                    <tr key={proposal.id}>
                      <td className="fw-bold">{proposal.proposal_number}</td>
                      <td>{getVersionBadge(proposal.proposal_number)}</td>
                      <td>₹ {parseFloat(proposal.transport_rate || 0).toLocaleString()} <small>/{proposal.rate_unit}</small></td>
                      <td>{proposal.advance_percentage}%</td>
                      <td className="fw-bold text-primary">
                        ₹ {parseFloat(proposal.total_freight_cost || 0).toLocaleString()}
                      </td>
                      <td>{getStatusBadge(proposal.status)}</td>
                      <td>
                        {new Date(proposal.submitted_at).toLocaleDateString()}
                      </td>
                      <td>
                        <div className="d-flex justify-content-center gap-2">
                          <Button
                            className="btn-gradient p-2 d-inline-flex"
                            size="sm"
                            onClick={() => handleViewDetails(proposal)}
                          >
                            <FaEye />
                          </Button>
                          {proposal.counter_proposal && (
                            <Button
                              className="btn-glass p-2 d-inline-flex text-warning"
                              size="sm"
                              onClick={() => handleViewCounter(proposal)}
                            >
                              <FaExchangeAlt />
                            </Button>
                          )}
                          {proposal.status === 'counter_proposed' && (
                            <Button
                              className="btn-gradient p-2 d-inline-flex"
                              size="sm"
                              onClick={() => handleRevise(proposal)}
                            >
                              <FaHistory />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
        )}
      </Container>

      {/* Proposal Details Modal */}
      <Modal
        isOpen={detailsModal}
        toggle={() => setDetailsModal(false)}
        size="lg"
        className="aurora-modal"
      >
        <ModalHeader toggle={() => setDetailsModal(false)} className="border-0">
          <span className="neo-text">Protocol Intelligence Detail</span>
        </ModalHeader>
        <ModalBody className="request-details-page p-4">
          {selectedProposal && (
            <>
              <Row className="g-4 mb-4">
                <Col md={6}>
                  <div className="detail-item">
                    <label className="detail-label">Manifest Identifier</label>
                    <p className="detail-value">
                      {selectedProposal.proposal_number}{' '}
                      {getVersionBadge(selectedProposal.proposal_number)}
                    </p>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="detail-item">
                    <label className="detail-label">Operational State</label>
                    <div className="mt-1">
                      {getStatusBadge(selectedProposal.status)}
                    </div>
                  </div>
                </Col>
              </Row>

              <Row className="g-4 mb-4">
                <Col md={6}>
                  <div className="detail-item">
                    <label className="detail-label">Rate Protocol</label>
                    <p className="detail-value text-primary">
                      ₹ {parseFloat(selectedProposal.transport_rate || 0).toLocaleString()} <small>/{selectedProposal.rate_unit}</small>
                    </p>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="detail-item highlight">
                    <label className="detail-label">Total Freight Quantum</label>
                    <p className="detail-value fw-bold text-success h4 m-0">
                      ₹ {parseFloat(selectedProposal.total_freight_cost || 0).toLocaleString()}
                    </p>
                  </div>
                </Col>
              </Row>

              <Row className="g-4 mb-4">
                <Col md={6}>
                  <div className="detail-item">
                    <label className="detail-label">Advance Allocation Ratio</label>
                    <p className="detail-value">
                      {selectedProposal.advance_percentage}%
                    </p>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="detail-item">
                    <label className="detail-label">Authorized Advance</label>
                    <p className="detail-value text-main fw-bold">
                      ₹ {parseFloat(selectedProposal.advance_amount || 0).toLocaleString()}
                    </p>
                  </div>
                </Col>
              </Row>

              <div className="detail-item mb-4">
                <label className="detail-label">Strategic Covenants</label>
                <div className="glass-effect p-3 rounded-3 border-glass">
                  <p className="text-main mb-0">{selectedProposal.payment_terms || 'Standard protocol applied.'}</p>
                </div>
              </div>

              {selectedProposal.revision_notes && (
                <div className="detail-item">
                  <label className="detail-label">Revision Intelligence</label>
                  <div className="alert-glass-info p-3 rounded-3">
                    <p className="text-main mb-0">{selectedProposal.revision_notes}</p>
                  </div>
                </div>
              )}
            </>
          )}
        </ModalBody>
        <ModalFooter className="border-0">
          <Button color="secondary" className="glass-effect" onClick={() => setDetailsModal(false)}>
            Abort
          </Button>
        </ModalFooter>
      </Modal>

      {selectedProposal && (
        <>
          <CounterProposal
            isOpen={counterProposalModal}
            toggle={() => setCounterProposalModal(false)}
            requestId={id}
            proposal={selectedProposal}
          />

          <ReviseProposal
            isOpen={reviseProposalModal}
            toggle={() => setReviseProposalModal(false)}
            requestId={id}
            proposal={selectedProposal}
          />
        </>
      )}
    </div>
  )
}

export default MyProposals
